/**
 * Pulse API Service
 * Returns Telegram signals enriched with pair addresses from DexScreener API
 * Uses DexScreener API (FREE, 300 req/min) instead of Moralis for pair addresses
 */

import { getTelegramSignals } from "./telegram-api";
import { getTokenPairs } from "./dexscreener-api";

// Removed extractTicker - now using DexScreener API for token metadata

/**
 * Get pair address and token metadata from DexScreener API for a token
 * @param {string} tokenAddress - Token contract address
 * @param {string} chainId - Chain ID (e.g., "solana", "ethereum")
 * @returns {Promise<Object|null>} Object with pairAddress, tokenName, tokenSymbol, tokenImage or null if not found
 */
const getTokenMetadataFromDexScreener = async (tokenAddress, chainId) => {
  try {
    // Convert chainId to DexScreener format
    const dexScreenerChainId = chainId === "solana" ? "solana" : 
      chainId === "0x1" ? "ethereum" :
      chainId === "0x38" ? "bsc" :
      chainId === "0x89" ? "polygon" :
      chainId === "0xa4b1" ? "arbitrum" :
      chainId === "0x2105" ? "base" :
      chainId === "0xa" ? "optimism" :
      chainId === "0xa86a" ? "avalanche" :
      chainId; // fallback to original

    const pairs = await getTokenPairs(dexScreenerChainId, tokenAddress);
    
    if (!pairs || pairs.length === 0) {
      return null;
    }

    // Find the pair with highest liquidity (most active/main pair)
    const bestPair = pairs.reduce((best, current) => {
      const bestLiq = best?.liquidity?.usd || 0;
      const currentLiq = current?.liquidity?.usd || 0;
      return currentLiq > bestLiq ? current : best;
    }, pairs[0]);

    // Extract token metadata from baseToken (the token we're looking for)
    // Check if our token address matches baseToken or quoteToken
    const tokenAddressLower = tokenAddress.toLowerCase();
    const baseTokenAddress = bestPair?.baseToken?.address?.toLowerCase();
    const quoteTokenAddress = bestPair?.quoteToken?.address?.toLowerCase();
    
    let tokenData = null;
    if (baseTokenAddress === tokenAddressLower) {
      // Our token is the baseToken
      tokenData = bestPair.baseToken;
    } else if (quoteTokenAddress === tokenAddressLower) {
      // Our token is the quoteToken
      tokenData = bestPair.quoteToken;
    } else {
      // Fallback to baseToken if addresses don't match (shouldn't happen, but safety)
      tokenData = bestPair.baseToken;
    }

    // Extract image from info object (DexScreener API structure)
    const tokenImage = bestPair?.info?.imageUrl || null;

    // Extract market cap from pair data (DexScreener returns marketCap as a number)
    const marketCap = bestPair?.marketCap ? parseFloat(bestPair.marketCap) : null;

    return {
      pairAddress: bestPair?.pairAddress || null,
      tokenName: tokenData?.name || null,
      tokenSymbol: tokenData?.symbol || null,
      tokenImage: tokenImage,
      marketCap: marketCap,
    };
  } catch (error) {
    console.warn(`Error fetching token metadata from DexScreener for ${tokenAddress}:`, error);
    return null;
  }
};

/**
 * Fetch Telegram signals and enrich with token metadata from DexScreener
 * Uses DexScreener API to get: pairAddress, tokenName, tokenSymbol, tokenImage
 * @param {string} chainId - Chain ID (default: "solana")
 * @param {string} channelGroup - Channel group to filter by ('trayd', 'kol', 'gmgn'). Defaults to 'trayd'
 * @returns {Promise<Array>} Array of signal objects with token metadata
 */
export const getPulseTokens = async (chainId = "solana", channelGroup = "trayd") => {
  try {
    // Fetch signals from Telegram scraper
    const signals = await getTelegramSignals(channelGroup);
    console.log(`📡 Fetched ${signals.length} raw signals from Telegram (channel group: ${channelGroup})`);

    if (signals.length === 0) {
      return [];
    }

    // Fetch more signals initially to account for filtering (some may not have pair addresses)
    // We want to return 8 tokens max, but need to fetch many more to account for filtering
    // Increased from 20 to 100 to ensure trayd signals aren't excluded due to filtering
    const MAX_TOKENS_TO_RETURN = 8;
    const MAX_SIGNALS_TO_FETCH = Math.min(signals.length, 100); // Increased from 20 to 100
    const limitedSignals = signals.slice(0, MAX_SIGNALS_TO_FETCH);
    
    // Fetch token metadata from DexScreener for each token (in parallel)
    console.log(`🔍 Fetching token metadata (name, symbol, image, pairAddress) from DexScreener for ${limitedSignals.length} tokens...`);
    
    const signalsWithMetadata = await Promise.all(
      limitedSignals.map(async (signal) => {
        if (!signal.ca) {
          console.log(`⚠️ Signal has no CA address, skipping`);
          return { ...signal, pairAddress: null, tokenName: null, tokenSymbol: null, tokenImage: null, marketCap: null };
        }

        const metadata = await getTokenMetadataFromDexScreener(signal.ca, chainId);
        
        if (metadata && metadata.pairAddress) {
          console.log(`✅ Found metadata for ${signal.ca.substring(0, 8)}...: ${metadata.tokenSymbol || 'N/A'} (${metadata.tokenName || 'N/A'}), marketCap: ${metadata.marketCap || 'N/A'}`);
        } else {
          console.log(`⚠️ No metadata found for ${signal.ca.substring(0, 8)}... (no pair address)`);
        }

        return {
          ...signal,
          pairAddress: metadata?.pairAddress || null,
          tokenName: metadata?.tokenName || null,
          tokenSymbol: metadata?.tokenSymbol || null,
          tokenImage: metadata?.tokenImage || null,
          marketCap: metadata?.marketCap || null,
        };
      })
    );

    // Filter to only include tokens with pair addresses (active trading pairs)
    const tokensWithMetadata = signalsWithMetadata.filter(s => s.pairAddress !== null);
    console.log(`📊 After pair address filter: ${tokensWithMetadata.length} tokens (${signalsWithMetadata.length - tokensWithMetadata.length} filtered out - no pair address)`);
    
    // Filter by market cap: only show tokens with market cap > 7000 USD
    // If market cap is not available, include the token (don't filter out)
    const MIN_MARKET_CAP = 7000;
    const tokensAboveMarketCap = tokensWithMetadata.filter(s => {
      const marketCap = s.marketCap;
      if (marketCap === null || marketCap === undefined) {
        // If market cap is not available, include it (don't filter out)
        console.log(`⚠️ Token ${s.ca?.substring(0, 8)}... has no market cap data, including anyway`);
        return true;
      }
      const passesFilter = marketCap > MIN_MARKET_CAP;
      if (!passesFilter) {
        console.log(`⚠️ Token ${s.ca?.substring(0, 8)}... has market cap $${marketCap.toFixed(2)} (below $${MIN_MARKET_CAP}), filtering out`);
      }
      return passesFilter;
    });
    
    console.log(`📊 After market cap filter: ${tokensAboveMarketCap.length} tokens (${tokensWithMetadata.length - tokensAboveMarketCap.length} filtered out - market cap < $${MIN_MARKET_CAP})`);
    
    // Limit to MAX_TOKENS_TO_RETURN (8) after filtering
    const finalTokens = tokensAboveMarketCap.slice(0, MAX_TOKENS_TO_RETURN);
    
    console.log(`✅ FINAL: Returning ${finalTokens.length} tokens`);
    console.log(`   - Raw signals from backend: ${signals.length}`);
    console.log(`   - Signals fetched for metadata: ${limitedSignals.length}`);
    console.log(`   - Signals with pair addresses: ${tokensWithMetadata.length}`);
    console.log(`   - Signals passing market cap filter: ${tokensAboveMarketCap.length}`);
    console.log(`   - Final tokens returned: ${finalTokens.length}`);
    
    return finalTokens;
  } catch (error) {
    console.error("Error fetching pulse tokens:", error);
    throw error;
  }
};



